import { z } from 'zod';
import { ServiceAddressSchema } from './address.js';
/**
 * E-SF20 — NEW SERVICE ESTABLISHMENT (new construction, no ESI ID exists yet).
 *
 * ── THE ONE FACT THAT SHAPES THIS WHOLE MODULE ─────────────────────────────
 * A REP CANNOT ASK A TDU TO INSTALL A METER. In ERCOT, new service is
 * established between the property owner (or their builder/electrician) and the
 * TDU directly: application → design → construction → municipal electrical
 * inspection (the Texas "green tag") → the TDU sets and energizes the meter →
 * the TDU creates the ESI ID and registers it with ERCOT. Only THEN can a REP
 * submit an 814_16 move-in. We are a spectator for the first four steps.
 *
 * Our own market-ops schema says the same thing: the REP-initiated 650 service
 * order set is `['NPD','RC','SH','SR','PO']` — there is no meter-set type — and
 * `esi_id` is REQUIRED on every one of them.
 *
 * So this is NOT a "submit an installation request and track it" contract. It is
 * DECLARE-AND-DETECT: the customer tells us a build is coming, we record it, and
 * we watch for the ESI ID to appear. Every name in this file is chosen to keep
 * that distinction visible — nothing here is called `request`, `submit` or
 * `installationStatus`, because we do none of those things.
 *
 * ── AC3 — NOTHING HERE EGRESSES AN ESI ID ──────────────────────────────────
 * By construction, and then by guard. A pending service point has no ESI ID (that
 * is its definition), and once one PUBLISHES the status response still hands back
 * only a display `premiseLabel` plus an OPAQUE `candidateId` — the same
 * redemption handle `/bff/address/lookup` mints. `assertResponseSchemasSafe`
 * refuses to boot on any response schema declaring `esiid` outside its
 * allow-list, and this module is deliberately not on that list.
 */
/** Local, matching `sign.ts` / `new-occupant.ts` — the contract has no shared date module. */
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'date must be YYYY-MM-DD');
/**
 * What kind of premise is being built.
 *
 * Drives the load-profile lookup (a townhome and a manufactured home on the same
 * square footage do not draw the same), and drives whether `unitCount` or
 * `squareFeet` is the meaningful scaling input.
 *
 * ⚠️ RESIDENTIAL ONLY IN v1. Commercial new construction is a different sales
 * motion (load study, demand metering, often an IDR meter) and is deliberately
 * absent rather than approximated — a storefront that accepts "COMMERCIAL" here
 * would produce a residential-profile estimate for a premise that will never
 * bill like one.
 */
export const NewServicePremiseType = z.enum([
    'SINGLE_FAMILY',
    'TOWNHOME',
    'MULTI_FAMILY',
    'MANUFACTURED',
]);
/**
 * The bounds on declared floor area.
 *
 * ── WHY THIS IS BOUNDED AT THE CONTRACT, NOT JUST VALIDATED IN THE UI ──────
 * Exactly the reasoning behind `DECLARED_MONTHLY_KWH_MIN/MAX` (estimate.ts):
 * this figure scales a load profile, the load profile becomes an estimated
 * annual bill, and the estimated annual bill sizes a PUCT §25.478 deposit —
 * whose statutory ceiling is one-fifth of it. A number that only the browser
 * checks is a number that is unchecked on every path that does not cross the
 * browser.
 *
 * 200 ft² is below any habitable new-build unit; 25,000 ft² is above any
 * residential build we would price from a profile rather than a load study. A
 * declaration outside the band is not clamped — it is refused, and the storefront
 * routes that customer to an operator, because silently pricing a 40,000 ft²
 * premise as a large house is the failure mode this bound exists to prevent.
 */
export const NEW_SERVICE_SQUARE_FEET_MIN = 200;
export const NEW_SERVICE_SQUARE_FEET_MAX = 25_000;
export const NewServiceSquareFeetSchema = z
    .number()
    .int()
    .min(NEW_SERVICE_SQUARE_FEET_MIN)
    .max(NEW_SERVICE_SQUARE_FEET_MAX);
/**
 * The customer-declared facts about a premise that does not exist yet.
 *
 * ── EVERY FIELD BUT `premiseType` IS OPTIONAL, AND THAT IS THE POINT ───────
 * On brand-new construction the county has no record, the builder may not have
 * finalised the plan, and the shopper is frequently a homeowner-to-be who knows
 * the street address and little else. A form that REFUSES to proceed without
 * square footage turns the one scenario this epic exists to unblock back into a
 * dead end. Absent fields fall back to a documented per-TDU default and the
 * estimate says so — an extra layer of estimation, disclosed, beats a blocked
 * funnel.
 *
 * ⚠️ `expectedOccupancyDate` IS SHAPE-CHECKED HERE AND WINDOW-CHECKED IN THE
 * BFF. A zod schema has no clock, and giving it one would make the contract
 * package time-dependent. The service layer holds the "not in the past, not more
 * than N years out" rule against its own `now`, exactly as `sign.ts` leaves the
 * start-date window to server-supplied `StartDateConstraints`.
 */
export const NewServiceBuildFactsSchema = z.object({
    premiseType: NewServicePremiseType,
    /** Scales the load profile for SINGLE_FAMILY / TOWNHOME / MANUFACTURED. */
    squareFeet: NewServiceSquareFeetSchema.optional(),
    /**
     * Scales MULTI_FAMILY, and is the early warning for the many-ESI-IDs case: a
     * multi-family build publishes N ESI IDs against ONE street address on one day,
     * so a declaration with `unitCount > 1` can never resolve to a single premise.
     */
    unitCount: z.number().int().min(1).max(500).optional(),
    /** When the customer expects to move in — paces the detection loop, not a promise. */
    expectedOccupancyDate: IsoDateSchema.optional(),
    /**
     * The municipal electrical permit, if the customer has it.
     *
     * Not decoration: the inspection green tag is the single most common cause of
     * new-service delay and the ONE thing the customer can act on. Capturing it up
     * front is what lets a delay notification say something useful instead of
     * "still waiting".
     */
    permitNumber: z.string().trim().min(1).max(40).optional(),
    builderName: z.string().trim().min(1).max(120).optional(),
});
/**
 * The lifecycle of a pending service point. OURS, not the TDU's.
 *
 * Every state is one WE can observe without the TDU telling us anything, which is
 * the constraint that makes it implementable at all:
 *
 *  - DECLARED                     — the customer told us a build is coming.
 *  - AWAITING_ESIID               — the detection loop is watching the registry.
 *  - ESIID_PUBLISHED              — an ESI ID appeared and matched this address.
 *  - ENERGIZED_PENDING_FIRST_READ — the meter is set; no usage has flowed yet.
 *  - LIVE                         — a first real read exists; ordinary rules apply.
 *  - ABANDONED                    — closed, by the customer or by the stale-record
 *                                   sweep. Terminal.
 *  - SUPERSEDED                   — the address turned out to ALREADY have service,
 *                                   so this record closes into the real ESI ID
 *                                   rather than sitting open beside it. Terminal.
 *
 * ⚠️ `SUPERSEDED` IS THE DUPLICATE GUARD, AND IT IS A STATE RATHER THAN A DELETE
 * so the "you already have service here" answer survives in history. Raising a
 * second declaration for a premise that is already served is the expensive,
 * common mistake in this scenario; a record that quietly vanished would let the
 * same customer make it again next week with nothing to show they had not.
 */
export const PendingServicePointState = z.enum([
    'DECLARED',
    'AWAITING_ESIID',
    'ESIID_PUBLISHED',
    'ENERGIZED_PENDING_FIRST_READ',
    'LIVE',
    'ABANDONED',
    'SUPERSEDED',
]);
/**
 * The handle for a pending service point, as it appears in a URL path segment.
 *
 * ── IT IS A CAPABILITY, NOT AN ID ──────────────────────────────────────────
 * Modelled on `OfferTokenSchema` and for the same reason: the customer reaches
 * their status page from a link in their own email, with no session and no login,
 * so the ref in that link IS the authorization. It must therefore be minted with
 * real entropy (`randomBytes(32).toString('base64url')`), never derived from the
 * address, the email or a sequence — a ref you can compute from a street address
 * is an oracle for who is building a house where.
 *
 * The bound is deliberately LOOSE (shape, not exact length) so the BFF does not
 * become a format oracle: a malformed guess and a well-formed unknown must be
 * refused identically. Keep the cap STRICTLY BELOW Fastify's `maxParamLength`
 * (512) so the application, not the transport, does the refusing.
 */
export const PendingServiceRefSchema = z
    .string()
    .min(1)
    .max(256)
    .regex(/^[A-Za-z0-9_-]+$/u, 'pending service ref must be URL-safe base64');
/**
 * `POST /bff/new-service/declare` — record a build the customer says is coming.
 *
 * ── WHY THE EMAIL IS REQUIRED HERE AND NOT DEFERRED ────────────────────────
 * A pending service point is useless without a way to reach its owner. The whole
 * value of the record is the message that arrives weeks later saying the meter is
 * live — and a storefront shopper is anonymous, has no account, and will not be
 * on this page when the ESI ID publishes. A declaration with no address to notify
 * is a row we poll for months and can never act on.
 *
 * The posture mirrors `/bff/lead` (which also takes a raw email on an anonymous
 * write): captcha-gated, idempotency-keyed, session-required, and redacted from
 * logs. Proving the address (OTP) is deliberately NOT required to declare — that
 * is a conversion gate on the wrong side of the wait — and belongs at the point
 * the customer returns to enrol.
 */
export const NewServiceDeclareRequestSchema = z.object({
    /** The address the lookup could not resolve. Re-sent so the record is self-contained. */
    address: ServiceAddressSchema,
    buildFacts: NewServiceBuildFactsSchema,
    email: z.string().email().max(254),
});
export const NewServiceDeclareResponseSchema = z.object({
    pendingRef: PendingServiceRefSchema,
    state: PendingServicePointState,
    /**
     * Whether the confirmation email — the one carrying the link back to this
     * record — actually reached a relay.
     *
     * ── WHY THE BROWSER IS TOLD, RATHER THAN LEFT TO ASSUME ───────────────────
     * The declaration and the email are two different things that can succeed
     * independently, and the record is the one that matters: an email failure must
     * not lose a customer's build. So the declare succeeds either way — but the
     * screen must then say something DIFFERENT. "We've emailed you a link" when no
     * relay accepted the message is the exact class of customer-facing falsehood
     * E-SF19 was written to clear out, and the customer would discover it days
     * later by finding nothing in their inbox and having nothing else to go on.
     *
     * `false` means the screen tells them to keep the reference on screen, because
     * that is the only copy of it they will get.
     */
    emailSent: z.boolean(),
});
/**
 * `GET /bff/new-service/:ref` — the anonymous status read, and the ONLY thing the
 * customer holds between declaring and enrolling.
 *
 * ── `overdue` IS A FLAG, NOT A STATE, AND THE DISTINCTION IS LOAD-BEARING ──
 * "Overdue" is not somewhere a build has got to — it is a judgement about how
 * long it has been where it already is. Modelling it as a state would mean a
 * record leaves AWAITING_ESIID to become OVERDUE and then has to be put back,
 * which loses the fact that nothing about the build actually changed. As a flag
 * it composes: a record is AWAITING_ESIID *and* overdue, and clearing the flag
 * when a status finally arrives costs nothing.
 *
 * ── WHAT IS DELIBERATELY ABSENT ────────────────────────────────────────────
 * No `esiid`, ever — see the module header. No TDU-supplied status or rejection
 * reason, because we receive neither (the module header again). No estimated
 * completion date FROM THE TDU: `expectedOccupancyDate` is the CUSTOMER'S OWN
 * declaration echoed back, and calling it anything else would present a guess as
 * a commitment from a party that never made one.
 */
export const NewServiceStatusResponseSchema = z.object({
    state: PendingServicePointState,
    declaredAt: IsoDateSchema,
    /** The customer's own declared date, echoed back. Null when they did not give one. */
    expectedOccupancyDate: IsoDateSchema.nullable(),
    /**
     * The municipal electrical permit the customer gave us, echoed back (US-14).
     *
     * ── WHY IT COMES BACK OUT AT ALL ───────────────────────────────────────
     * The inspection green tag is the single most common cause of new-service
     * delay and the ONE thing the customer can act on. Showing the permit number
     * on the page turns "your build is late, ask your utility" — which is barely
     * more useful than silence — into a sentence they can take to their city
     * inspections office and their builder.
     *
     * Safe to return: a building permit is public record, it identifies a property
     * rather than a person, and the caller already holds the ref that resolves the
     * record it belongs to. Null when they did not give one.
     */
    permitNumber: z.string().min(1).nullable(),
    /** Pending longer than its configured threshold. Composes with `state`; never replaces it. */
    overdue: z.boolean(),
    /**
     * Display-only premise label, present from ESIID_PUBLISHED onward. The human
     * confirmation that we matched the right premise — and the surface on which a
     * mismatched unit or address gets noticed before anyone enrols on it.
     */
    premiseLabel: z.string().min(1).nullable(),
    /**
     * The opaque redemption handle the funnel needs, minted once the premise
     * resolves — the SAME kind of handle `/bff/address/lookup` returns, so the
     * customer rejoins the ordinary enrollment path rather than a parallel one.
     * Null until then.
     */
    candidateId: z.string().min(1).nullable(),
    /**
     * Set only on SUPERSEDED: this address already had service, so there was
     * nothing to wait for. Carries no premise detail — the customer is sent back
     * through the ordinary address lookup, which is the surface that is allowed to
     * resolve a premise.
     */
    supersededByExistingService: z.boolean(),
});
//# sourceMappingURL=new-service.js.map